/**
 * Reskins this deployment from one JSON document supplied at runtime.
 *
 * A demo marketplace built for a sales prospect has to look like their idea
 * rather than like a template, and it has to look like it within the minute it
 * takes to send them the link. Baking the skin into the bundle cannot do that:
 * a build of this template is minutes, so a per-prospect build means the link
 * in the reply does not work yet when the reply is written.
 *
 * Console cannot do it either, and deliberately is not asked to. The marketplaces
 * behind these demos are reused, several prospects pass through the same one, and
 * a Console write is marketplace-wide state that the next prospect would inherit.
 * The skin therefore lives outside the marketplace altogether, in one env var on
 * the running service, and reskinning is a service update rather than a build or
 * a Console edit.
 *
 * The document is merged into the hosted-asset bundle rather than over the
 * finished config, so every validator in configHelpers still runs on it. A demo
 * skin reaches the app by exactly the route a Console edit would, which is what
 * keeps this from being a second configuration system.
 */

/**
 * The raw overlay document, or null when this deployment is not a demo.
 *
 * One global, read the same way on both sides, rather than the env var on the
 * server and the global in the browser. This code is bundled, and webpack's
 * DefinePlugin rewrites every `process.env.X` it sees into a literal drawn from
 * create-react-app's allowed list, which is REACT_APP_* and NODE_ENV. It does
 * that to the server bundle as well as the browser one, so a bundled read of
 * `process.env.DEMO_CONFIG` is not a read at all: it compiles to `undefined`.
 *
 * The symptom was a demo that skinned itself in the browser and served the bare
 * template from the server, which is the worst of the three possible outcomes,
 * because the page arrives wearing the wrong marketplace and then corrects
 * itself in front of the prospect.
 *
 * So the env var is read exactly once, in `server/index.js`, which is plain
 * Node and never passes through webpack, and assigned here. The browser gets
 * the same global from the script tag `server/renderer.js` injects.
 */
const rawOverlay = () =>
  typeof globalThis !== 'undefined' && globalThis.__DEMO_CONFIG__
    ? globalThis.__DEMO_CONFIG__
    : null;

/**
 * Decode base64 to a UTF-8 string on either side of the wire.
 *
 * Not `atob` alone: it yields one character per byte, so a marketplace named
 * "Cafe Collectif" survives and one named "Cafe Collectif" with an accent comes
 * out mangled. The prospect's own name is the single most visible string on the
 * page, so it is decoded properly rather than nearly.
 */
const decodeBase64Utf8 = b64 => {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(b64, 'base64').toString('utf8');
  }
  const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
  return new TextDecoder('utf-8').decode(bytes);
};

/**
 * Parse the document, or throw.
 *
 * The value is base64-encoded JSON rather than JSON. It reaches the service as
 * a `gcloud run services update --update-env-vars` argument, where the value is
 * comma-delimited and a raw JSON object is nothing but commas, and it reaches
 * the browser inside a script tag, where raw JSON can carry the characters that
 * close one. Base64 has neither problem, and it has one encoding rather than a
 * different escaping rule per hop.
 *
 * Throwing is the point. A demo whose skin failed to parse renders as the
 * unskinned template, which is a marketplace with someone else's name on it
 * being shown to a prospect. Failing the request instead means the deploy
 * verification catches it before the link is ever sent.
 */
const parseOverlay = raw => {
  if (!raw) {
    return null;
  }
  if (typeof raw === 'object') {
    return raw;
  }
  let json;
  try {
    json = decodeBase64Utf8(raw);
  } catch (e) {
    throw new Error(`DEMO_CONFIG is not valid base64: ${e.message}`);
  }
  let parsed;
  try {
    parsed = JSON.parse(json);
  } catch (e) {
    throw new Error(`DEMO_CONFIG did not decode to valid JSON: ${e.message}`);
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('DEMO_CONFIG must decode to a JSON object.');
  }
  return parsed;
};

/**
 * Deep merge where the overlay wins, and an array in the overlay replaces
 * rather than extends.
 *
 * Arrays replace because every array in this configuration is a closed set
 * rather than a collection: the listing types a prospect's marketplace offers
 * are exactly the ones their brief named, and appending them to the template's
 * defaults would show them a marketplace selling both their idea and a
 * bicycle. The same holds for listing fields and search filters.
 */
const deepMerge = (base, overlay) => {
  if (Array.isArray(overlay) || overlay === null || typeof overlay !== 'object') {
    return overlay;
  }
  if (Array.isArray(base) || base === null || typeof base !== 'object') {
    return { ...overlay };
  }
  return Object.keys(overlay).reduce(
    (merged, key) => ({ ...merged, [key]: deepMerge(base[key], overlay[key]) }),
    { ...base }
  );
};

/**
 * True when this deployment is carrying a demo skin. Used to keep demo-only
 * behaviour (a text wordmark instead of the template's logo) off a real
 * client's deployment of this same branch.
 */
export const isDemoDeployment = () => parseOverlay(rawOverlay()) !== null;

/**
 * The prospect-facing name of this demo, or null. Read by the text wordmark.
 */
export const demoMarketplaceName = () => {
  const overlay = parseOverlay(rawOverlay());
  const name = overlay?.defaults?.marketplaceName;
  return typeof name === 'string' && name.length > 0 ? name : null;
};

/**
 * Apply the skin to the two inputs of mergeConfig.
 *
 * Two sections rather than one because mergeConfig genuinely has two inputs
 * with different shapes: `assets` carries what Console would have served and
 * goes through the asset validators, `defaults` carries the handful of values
 * that only exist as build-time app config, `marketplaceName` above all. A
 * single flat document would have to guess which of the two a key belongs to.
 *
 * Returns the inputs unchanged when there is no overlay, so a non-demo
 * deployment of this branch behaves exactly as it did before.
 */
export const applyDemoOverlay = (configAsset, defaultConfigs) => {
  const overlay = parseOverlay(rawOverlay());
  if (!overlay) {
    return { configAsset, defaultConfigs };
  }
  return {
    configAsset: deepMerge(configAsset, overlay.assets || {}),
    defaultConfigs: deepMerge(defaultConfigs, overlay.defaults || {}),
  };
};
